import { Drawer, Button, Spin, Space, Tag } from 'antd';
import { CheckOutlined, MinusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import Tiptap from '../editor/Tiptap';
import { fmtTaipei } from '../../pages/article-list/status';
import { useDrawerWidth } from '../../hooks/useIsMobile';

interface Props {
  articleId: string | null;
  picked: boolean;
  onTogglePick: (id: string) => void;
  onClose: () => void;
}

/**
 * 挑選關聯新聞時的「這是哪一篇」預覽。
 * 只看標題常常認不出來（同系列報導標題很像），所以點整列就開這個看封面與內文。
 * 可直接在這裡加入／移除關聯，不必關掉再回表格找那一列。
 *
 * 開在「挑選關聯新聞」那個全螢幕 Drawer 之上，因此 `push={false}`——
 * 預設會把父層往旁邊推，但父層是滿版的，推了只會抖一下沒有意義。
 */
export default function RelatedPreviewDrawer({ articleId, picked, onTogglePick, onClose }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['article', articleId],
    queryFn: () => api.getArticle(articleId!),
    enabled: !!articleId,
  });
  const a = data?.article;
  const width = useDrawerWidth(860);

  return (
    <Drawer
      title="預覽"
      open={!!articleId}
      onClose={onClose}
      width={width}
      push={false}
      footer={
        <Space>
          <Button onClick={onClose}>關閉</Button>
          <Button
            type={picked ? 'default' : 'primary'}
            danger={picked}
            icon={picked ? <MinusOutlined /> : <CheckOutlined />}
            onClick={() => articleId && onTogglePick(articleId)}
          >
            {picked ? '移除關聯' : '加入關聯'}
          </Button>
        </Space>
      }
    >
      {isLoading || !a ? (
        <Spin className="block py-16 text-center" />
      ) : (
        <div>
          <h3 className="mb-2 text-lg font-semibold leading-snug text-[#1f2733]">{a.title}</h3>
          <Space size={[8, 4]} wrap className="mb-3 text-xs">
            {a.categoryNames && <Tag>{a.categoryNames}</Tag>}
            {a.authorName && <span className="text-gray-500">{a.authorName}</span>}
            <span className="text-gray-400">
              {a.publishedAt ? fmtTaipei(a.publishedAt) : `更新於 ${fmtTaipei(a.updatedAt)}`}
            </span>
          </Space>
          {data.coverUrl && (
            <img
              src={data.coverUrl}
              alt=""
              className="mb-3 max-h-64 w-full rounded-lg object-cover"
            />
          )}
          <Tiptap initialJson={a.bodyJson} initialHtml={a.bodyHtmlLegacy} readOnly />
        </div>
      )}
    </Drawer>
  );
}
